import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DeleteDialog from 'Components/DeleteDialog'
import FieldErrors from 'Components/FieldErrors'
import SaveDialog from 'Components/SaveDialog'
import StyledAccordion from 'Components/StyledAccordion'
import StyledForm from 'Components/StyledForm'
import {
  createDeviceConfig,
  type DeviceConfigDetailShape,
  type DeviceTypeShape,
  deleteDeviceConfig,
  editDeviceConfig,
  fetchDeviceTypeOptions,
  fetchDeviceTypes,
  fetchSingleDeviceConfig
} from 'Services/SalesManagement/Features/WalttiPosRemoteConfig/walttiPosRemoteConfigSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { getSubstringLineNumber } from 'Util/getSubstringLineNumber'
import jsonTreeTheme from 'Util/jsonTreeTheme'
import SnackbarUtils from 'Util/Snackbar'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HomeIcon from '@mui/icons-material/Home'
import PointOfSaleSharpIcon from '@mui/icons-material/PointOfSaleSharp'
import PublicIcon from '@mui/icons-material/Public'
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote'
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  DialogContentText,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import type { Ace } from 'ace-builds'
import Ajv from 'ajv'
import { type FormEvent, type default as React, useEffect, useRef, useState } from 'react'
import AceEditor from 'react-ace'
import { JSONTree } from 'react-json-tree'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

import 'ace-builds/src-noconflict/ext-language_tools'
import 'ace-builds/src-noconflict/ext-searchbox'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-github'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'

export default function WalttiPosDeviceDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/walttipos-remote-config`
  const dispatch = useAppDispatch()
  const instanceIdParam = useParams<{ id: string }>().id
  const deviceTypeParam = parseInt(useParams<{ type: string }>().type as string, 10)
  const navigate = useNavigate()

  const {
    singleDevice,
    singleDeviceLoading,
    deviceTypes,
    deviceTypeOptions,
    deviceTypesLoading,
    deviceTypeOptionsLoading,
    submitLoading
  } = useTypedSelector(state => state.walttiPosRemoteConfig)

  const initialDeviceDetails: DeviceConfigDetailShape = {
    machine_id: '',
    name: '',
    type_configuration: deviceTypeParam,
    device_fields: {}
  }

  const fieldErrorsDefaultState = {
    type: [],
    name: [],
    machine_id: []
  }

  const formRef = useRef<HTMLFormElement>(null)

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [deviceDetails, setDeviceDetails] = useState<DeviceConfigDetailShape>(initialDeviceDetails)
  const [deviceType, setDeviceType] = useState<DeviceTypeShape | null>(null)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleteActionLoading, setIsDeleteActionLoading] = useState(false)
  const [deviceConfig, setDeviceConfig] = useState('{}')
  const [commonConfig, setCommonConfig] = useState<Record<string, unknown> | undefined>()
  const [deviceConfigError, setDeviceConfigError] = useState<Ace.Annotation[]>([])
  const isNewInstance = !instanceIdParam
  const previousLocation = usePreviousLocation(`${baseUrl}?type_configuration=${deviceType?.type}`)

  const pageTitle = isNewInstance ? t`Create new configuration` : t`Edit configuration`

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    dispatch(fetchDeviceTypeOptions())
    dispatch(fetchDeviceTypes())
      .then(unwrapResult)
      .then(result => {
        setSelectedDeviceType(deviceTypeParam, result)
      })
    if (!isNewInstance) {
      dispatch(fetchSingleDeviceConfig({ machine_id: instanceIdParam }))
    }
  }, [dispatch])

  useEffect(() => {
    if (deviceType) {
      if (isNewInstance) {
        const filteredConfig = getFilteredDeviceConfig({}, deviceType.full_schema.required)
        setDeviceDetails(prevState => ({ ...prevState, device_fields: { ...filteredConfig } }))
        updateJsonEditor(filteredConfig)
      } else if (singleDevice) {
        const filteredConfig = getFilteredDeviceConfig(
          singleDevice.device_fields,
          deviceType.full_schema.required
        )
        setDeviceDetails({ ...singleDevice, device_fields: { ...filteredConfig } })
        updateJsonEditor(filteredConfig)
      }
    }
  }, [singleDevice, deviceType])

  useEffect(() => {
    if (deviceConfig) {
      validateSchema(deviceConfig)
    }
  }, [deviceConfig])

  /**
   * Finds the selectedDeviceType from all the deviceTypes fetched from the API and removes the
   * required fields (from the selectedDeviceType's schema) that are already in the common configuration
   * @param {number} typeConfigurationNumber - The number of the type configuration.
   * @param {DeviceTypeShape[]} allDeviceTypes - All possible device types.
   */
  const setSelectedDeviceType = (
    typeConfigurationNumber: number,
    allDeviceTypes: DeviceTypeShape[]
  ) => {
    const selectedDeviceType = allDeviceTypes.find(obj => {
      return obj.type === typeConfigurationNumber
    })
    if (selectedDeviceType) {
      const commonConfigFieldKeys = Object.keys(selectedDeviceType.common_fields)
      const filteredDeviceType = { ...selectedDeviceType }
      // Remove required fields that are already in the common configuration
      const filteredRequiredFields = filteredDeviceType.full_schema.required.filter(
        val => !commonConfigFieldKeys.includes(val)
      )

      setDeviceType({
        ...selectedDeviceType,
        full_schema: { ...selectedDeviceType.full_schema, required: filteredRequiredFields as [] }
      })
    } else {
      SnackbarUtils.error(t`Default configuration not found for device type.`)
    }
  }

  /**
   * Appends the missing required fields to the device configuration as null values
   * @param originalConfig - Original device configurations.
   * @param requiredDeviceFields - Fields required by the configuration schema
   */
  const getFilteredDeviceConfig = (
    originalConfig: Record<string, unknown>,
    requiredDeviceFields: []
  ) => {
    const deviceConfigFieldKeys = Object.keys(originalConfig)
    const filteredConfig = { ...originalConfig }

    requiredDeviceFields.forEach((val: string) => {
      if (!deviceConfigFieldKeys.includes(val)) {
        // Add missing required fields as null values
        Object.assign(filteredConfig, { [val]: null })
      }
    })
    return filteredConfig
  }

  /**
   * The used JSON editor component is uncontrolled, so we need to update its content with ref.
   * @param config
   */
  const updateJsonEditor = (config: Record<string, unknown>) => {
    setDeviceConfig(JSON.stringify(config, null, '\t'))
    setCommonConfig(deviceType?.masked_common_fields)
  }

  const handleSelectChange = (event: SelectChangeEvent) => {
    const { value } = event.target
    setSelectedDeviceType(parseInt(value), deviceTypes)
    if (isNewInstance) {
      navigate(`${baseUrl}/create/${value}`)
    } else {
      navigate(`${baseUrl}/${singleDevice?.machine_id}/${value}`)
    }
  }

  /**
   * Shows/hides the secret (masked) fields. The used JSON editor component is uncontrolled, so we need to update its content with ref.
   * @param event
   */
  const handleToggleSecretConfigs = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setCommonConfig(deviceType?.common_fields)
    } else {
      setCommonConfig(deviceType?.masked_common_fields)
    }
  }

  const handleDeviceNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeviceDetails(prevState => ({ ...prevState, name: event.target.value }))
  }

  const handleMachineIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeviceDetails(prevState => ({ ...prevState, machine_id: event.target.value }))
  }

  const handleChange = (value: string, _e: unknown) => {
    setDeviceConfig(value)
  }

  /**
   * We have wrapped the editable content with a form and submit them through the form ref.
   * This way the browser's default validation for required fields is triggered.
   */
  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const newDeviceConfig = {
      type_configuration: deviceDetails.type_configuration,
      machine_id: deviceDetails.machine_id,
      name: deviceDetails.name,
      device_fields: JSON.parse(deviceConfig)
    }
    if (isNewInstance) {
      dispatch(createDeviceConfig(newDeviceConfig))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(`${baseUrl}?type_configuration=${newDeviceConfig.type_configuration}`)
          SnackbarUtils.success(t`Configuration successfully added.`)
        })
        .catch(error => {
          setIsSaveDialogOpen(false)
          window.scrollTo(0, 0)
          if (error.non_field_errors && error.non_field_errors.length > 0) {
            SnackbarUtils.error(
              t`There was an error creating the configuration: ` + error.non_field_errors[0]
            )
          } else if (error) {
            SnackbarUtils.error(t`There was an error creating the configuration.`)
            setFieldErrors({
              ...error
            })
          } else {
            SnackbarUtils.error(t`There was an error creating the configuration.`)
          }
        })
    } else {
      dispatch(editDeviceConfig(newDeviceConfig))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(`${baseUrl}?type_configuration=${newDeviceConfig.type_configuration}`)
          SnackbarUtils.success(t`Configuration successfully edited.`)
        })
        .catch(error => {
          setIsSaveDialogOpen(false)
          window.scrollTo(0, 0)
          if (error.non_field_errors && error.non_field_errors.length > 0) {
            SnackbarUtils.error(
              t`There was an error editing the configuration: ` + error.non_field_errors[0]
            )
          } else if (error) {
            SnackbarUtils.error(t`There was an error editing the configuration.`)
            setFieldErrors({
              ...error
            })
          } else {
            SnackbarUtils.error(t`There was an error editing the configuration.`)
          }
        })
    }
  }

  const handleDelete = () => {
    setIsDeleteActionLoading(true)
    dispatch(
      deleteDeviceConfig({
        machine_id: deviceDetails.machine_id
      })
    )
      .then(unwrapResult)
      .then(() => {
        setIsDeleteDialogOpen(false)
        SnackbarUtils.success(t`Configuration successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the configuration.`)
      })
      .finally(() => {
        setIsDeleteActionLoading(false)
        navigate(`${baseUrl}?type_configuration=${deviceType?.type}`)
      })
  }

  const validateSchema = (value: string) => {
    if (!deviceType?.full_schema) return
    // Cannot validate if the JSON is not valid
    let jsonValue = {}
    try {
      jsonValue = JSON.parse(value)
    } catch (e) {
      console.error(e)
      return
    }

    const ajv = new Ajv({ allErrors: true, verbose: true, strictSchema: true })
    const validate = ajv.compile(deviceType.full_schema)
    const isValid = validate(jsonValue)
    if (!isValid) {
      const errors = validate.errors?.map(err => {
        return err
      })
      setFieldErrors(prevState => ({
        ...prevState,
        device_fields: errors
      }))
    } else {
      setFieldErrors(prevState => ({
        ...prevState,
        device_fields: []
      }))
    }

    const errors: Ace.Annotation[] =
      validate.errors?.map(err => {
        return {
          row: (getSubstringLineNumber(value, err.instancePath.split('/')[1]) || 1) - 1,
          column: 0,
          text: err.message ?? 'Unknown error',
          type: 'error'
        }
      }) ?? []
    setDeviceConfigError(errors)
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL,
      icon: <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
    },
    {
      title: t`Points of sale's remote configurations`,
      link: baseUrl,
      icon: <SettingsRemoteIcon sx={{ mr: 0.5 }} fontSize="inherit" />
    },
    {
      title: pageTitle
    }
  ]

  const isSingleDeviceLoading = singleDeviceLoading === 'pending'
  const isSubmitLoading = submitLoading === 'pending'
  const isLoading =
    isSingleDeviceLoading ||
    deviceTypesLoading === 'pending' ||
    deviceTypeOptionsLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <BackButton label={t`Back`} link={previousLocation} />
          {!isNewInstance && (
            <>
              <DeleteButton
                onClick={() => setIsDeleteDialogOpen(!isDeleteDialogOpen)}
              >{t`Delete configuration`}</DeleteButton>
              <DeleteDialog
                open={isDeleteDialogOpen}
                handleClose={() => setIsDeleteDialogOpen(false)}
                handleSubmit={handleDelete}
                loading={isDeleteActionLoading}
                title={t`Delete configuration`}
              >
                <Typography component="span" color="text.primary">
                  {t`Are you sure you want to delete the configuration?`}
                </Typography>
              </DeleteDialog>
            </>
          )}
        </Box>
        <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
          <Box sx={{ mb: 2 }}>
            {isLoading ? (
              <LinearProgress />
            ) : (
              <Box sx={{ mb: 2 }}>
                <Box>
                  <FormControl sx={{ minWidth: 200 }} size="small" required>
                    <InputLabel>{t`Device type`}</InputLabel>
                    <Select
                      required
                      value={deviceType ? deviceType.type.toString() : ''}
                      label={t`Device type`}
                      autoWidth
                      error={fieldErrors.type?.length > 0}
                      onChange={handleSelectChange}
                    >
                      {Object.keys(deviceTypeOptions.type).map(option => (
                        <MenuItem key={option} value={option.toString()}>
                          {deviceTypeOptions.type[option]}
                        </MenuItem>
                      ))}
                    </Select>
                    <FieldErrors error={fieldErrors.type} identifier="type" />
                  </FormControl>
                </Box>
                <Box>
                  <TextField
                    required
                    sx={{ minWidth: 200 }}
                    disabled={!isNewInstance}
                    label={t`Machine ID`}
                    value={deviceDetails.machine_id}
                    onChange={handleMachineIdChange}
                    error={fieldErrors.machine_id?.length > 0}
                    helperText={
                      isNewInstance && t`Note that this field cannot be changed afterwards.`
                    }
                  />
                  <FieldErrors error={fieldErrors.machine_id} identifier="machine_id" />
                </Box>
                <Box>
                  <TextField
                    required
                    sx={{ minWidth: 200 }}
                    label={t`Device name`}
                    value={deviceDetails.name}
                    onChange={handleDeviceNameChange}
                    error={fieldErrors.name?.length > 0}
                  />
                  <FieldErrors error={fieldErrors.name} identifier="name" />
                </Box>
              </Box>
            )}
            <StyledAccordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <PublicIcon sx={{ mr: 1 }} color="primary" />
                <Typography>{t`Common configurations`}</Typography>
              </AccordionSummary>
              <Divider />
              {isLoading ? (
                <LinearProgress />
              ) : (
                <AccordionDetails>
                  <Typography sx={{ mb: 3, mt: 2 }}>
                    {t`The common configurations are the default configurations that are inherited by all the devices of this type. You can override these values in the device configurations section.`}
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch defaultChecked={false} onChange={handleToggleSecretConfigs} />
                      }
                      label={t`Show masked fields`}
                    />
                  </Box>
                  <JSONTree
                    theme={jsonTreeTheme}
                    invertTheme
                    data={commonConfig ?? deviceType?.masked_common_fields}
                  />
                </AccordionDetails>
              )}
            </StyledAccordion>
          </Box>
          <Box sx={{ mb: 2 }}>
            <StyledAccordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <PointOfSaleSharpIcon sx={{ mr: 1 }} color="primary" />
                <Typography>{t`Device configurations`}</Typography>
              </AccordionSummary>
              <Divider />
              {isLoading ? (
                <LinearProgress />
              ) : (
                <AccordionDetails>
                  <Typography sx={{ mb: 3, mt: 2 }}>
                    {t`The device configurations are device specific and are only used by the device with the given machine ID. In the device configurations you can also override common configurations just by adding the fields here.`}
                  </Typography>
                  <AceEditor
                    mode="json"
                    theme="github"
                    name="device-editor"
                    width="100%"
                    fontSize={14}
                    onChange={handleChange}
                    value={deviceConfig}
                    editorProps={{ $blockScrolling: true }}
                    setOptions={{
                      enableBasicAutocompletion: true,
                      enableAutoIndent: true,
                      useWorker: false
                    }}
                    annotations={deviceConfigError}
                  />
                </AccordionDetails>
              )}
            </StyledAccordion>
          </Box>
        </StyledForm>
      </Box>
      <SaveDialog
        open={isSaveDialogOpen}
        handleClose={() => setIsSaveDialogOpen(false)}
        handleSubmit={handleSubmit}
        loading={isSubmitLoading}
        title={t`Save configuration`}
      >
        <DialogContentText>
          <Typography component="span" color="text.primary">
            {t`Are you sure you want to save the configuration?`}
          </Typography>
        </DialogContentText>
      </SaveDialog>
      <CustomLoadingButton
        isLoading={isSubmitLoading}
        onClick={() => setIsSaveDialogOpen(true)}
        size="large"
        iconPosition="end"
      >
        {t`Save configuration`}
      </CustomLoadingButton>
    </Box>
  )
}
